import React, { useCallback, useState } from "react";
import { useForm } from "react-hook-form-7";
import useViewer from "../../hooks/useViewer";
import useTranslations from "../../hooks/useTranslations";
import { CloseIcon, EditIcon, SaveIcon } from "../icons";
import { Button, H4, Textarea } from "../wombat-ui";
import { GetUserDocument, useInsertUser, UserReviewFragment } from '../../graphql/generated/definitions';

type UserNotesSectionProps = {
  user: UserReviewFragment;
};

type UserNotesInput = {
  id: string;
  notes: string;
};

const UserNotesSection: React.FC<UserNotesSectionProps> = ({
  user,
}) => {
  const t = useTranslations();
  const viewer = useViewer();

  const [editingNotes, setEditingNotes] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    setValue,
    setError,
  } = useForm<UserNotesInput>({
    defaultValues: {
      notes: user.notes,
    },
  });

  const [updateUserNotes, { loading }] = useInsertUser({
    refetchQueries: [{ query: GetUserDocument, variables: { id: user.id } }],
  });

  const onSubmit = useCallback((data) => {
    updateUserNotes({
      variables: {
        input: { ...data, id: user.id },
      },
    })
      .then(() => {
        setEditingNotes(false);
      })
      .catch((error) => {
        const field = error.graphQLErrors?.[0]?.field;
        const errorMessage = t(
          `forms.errors.${error.graphQLErrors?.[0]?.details?.[0]?.error}`,
          { value: error.graphQLErrors?.[0]?.details?.[0]?.value }
        ) as string;
        setError(field, {
          message: errorMessage,
        });
      });
  }, []);

  if (!user) {
    return null;
  }

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <H4>
            {t("collaborator.notes")}
          </H4>
          <p className="text-xs font-medium text-gray-400">
            {t("collaborator.notesDescription", {
              name: user.name,
            })}
          </p>
        </div>
        <div>
          <Button
            id="saveNotes"
            className={`mx-1 ${!editingNotes && "hidden"}`}
            type="submit"
            variant="ghost"
            loading={loading}
            form="updateUserNotesForm"
            icon={SaveIcon}
          />
          <Button
            id="closeNotes"
            className={!editingNotes && "hidden"}
            variant="ghost"
            form="updateUserNotesForm"
            onClick={() => {
              setEditingNotes(false);
              setValue("notes", user.notes);
            }}
            icon={CloseIcon}
          />
          <Button
            id="editNotes"
            className={editingNotes && "hidden"}
            variant="ghost"
            type="button"
            onClick={() => setEditingNotes(true)}
            disabled={!viewer.isCapableOfUpdatingUserNotes}
            icon={EditIcon}
          />
        </div>
      </div>
      <div>
        <form
          className="mt-3 mb-4 space-y-4"
          id="updateUserNotesForm"
          onSubmit={handleSubmit(onSubmit)}
        >
          <Textarea
            id="notes"
            {...register('notes')}
            disabled={!editingNotes}
            label={t("forms.fields.notes") as string}
            srOnlyLabel
            placeholder={t("forms.fields.notes") as string}
          />
        </form>
      </div>
    </>
  );
};

export default UserNotesSection;
